import MapboxViewer from "@/components/mapbox/MapboxViewer";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import { autobind } from "core-decorators";
import { MapboxBuildingObject } from "@/components/mapbox/Lib/Objects/MapboxBuildingObject";
// import * as mapboxgl from "mapbox-gl";
// import { degToRad } from "three/src/math/MathUtils.js";

class EditorBuildingController extends BaseController {
  public static name = "EditorBuildingController";
  public static event = {
    COMPLETE: "COMPLETE",
  };

  private _selectedFeature: MapboxBuildingObject | null = null;
  private _isEditing: boolean = false;
  private _isRotating: boolean = false;
  private _isOnRotationHandle: boolean = false;
  private _dragStartPosition: { lng: number; lat: number } | null = null;
  private _originalCoordinates: number[][] | null = null;
  private _originalRotationDeg: number = 0;
  private _rotationStartAngle: number = 0;
  private _rotationCenter: { lng: number; lat: number } | null = null;

  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
    this._mapboxViewer = mapboxViewer;
    this._canvas.addEventListener("pointerdown", this.onPointerDownController);
    this._canvas.addEventListener("pointermove", this.onPointerMoveController);
    this._canvas.addEventListener("keydown", this.handleKeyDown);
  }

  get selectedFeature() {
    return this._selectedFeature;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
  }

  public get enabled() {
    return this._enabled;
  }

  @autobind
  attached(object: MapboxBuildingObject) {
    if (!this._enabled) {
      return;
    }
    if (object.content.config?.isLocked) {
      return;
    }
    this._selectedFeature = object;
    // Enable editing mode
    this._selectedFeature.setSelected();
  }

  @autobind
  onPointerDownController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._selectedFeature) return;

    const canvasRect = this._canvas.getBoundingClientRect();
    this._onDownPosition.x = event.clientX - canvasRect.left;
    this._onDownPosition.y = event.clientY - canvasRect.top;

    // Store drag start position in geographic coordinates
    const dragStartLngLat = this._mapboxViewer._mapViewer.unproject([
      this._onDownPosition.x,
      this._onDownPosition.y,
    ]);
    this._dragStartPosition = {
      lng: dragStartLngLat.lng,
      lat: dragStartLngLat.lat,
    };

    // Store original values
    this._originalCoordinates = this._selectedFeature.content.coordinates.map(
      (coord) => [...coord]
    );
    this._originalRotationDeg =
      this._selectedFeature.content.properties?.rotation || 0;

    // Check if we're clicking on the building (for rotation)
    const buildingCenter = this._selectedFeature.content.coordinates[0];
    this._rotationCenter = {
      lng: buildingCenter[0],
      lat: buildingCenter[1],
    };

    // Calculate initial rotation angle
    this._rotationStartAngle = this.calculateAngleFromCenter(
      this._dragStartPosition,
      this._rotationCenter
    );

    // Detect if clicking the rotation handle
    const features = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      [this._onDownPosition.x, this._onDownPosition.y],
      {
        filter: [
          "all",
          ["==", ["get", "app"], "logistic-app"],
          ["==", ["get", "subtype"], "rotation_handle"],
          ["==", ["get", "contentId"], this._selectedFeature.contentId],
        ],
      }
    );
    this._isOnRotationHandle = features && features.length > 0;
    if (this._isOnRotationHandle) {
      this._isRotating = true;
      this._isDragging = false;
    }

    // Detect if clicking the rotation handle (shared layer)
    const hitRotation = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      [this._onDownPosition.x, this._onDownPosition.y],
      {
        layers: ["building-rotation-handles"],
      }
    );

    // Check if the clicked rotation handle belongs to the selected building
    if (hitRotation && hitRotation.length > 0) {
      const feature = hitRotation[0];
      if (feature.properties?.contentId === this._selectedFeature.contentId) {
        this._isOnRotationHandle = true;
        this._isRotating = true;
        this._isDragging = false;
        this._mapboxViewer.toggleCamera(false);
        this._isEditing = true;
        this._canvas.addEventListener("pointerup", this.onPointerUpController);
        return;
      }
    }

    // Detect if clicking the polygon footprint or extrusion (shared layers)
    const hitPolygon = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      [this._onDownPosition.x, this._onDownPosition.y],
      {
        layers: [
          "building-polygons",
          "building-polygons-outline",
          "building-extrusion",
        ],
      }
    );

    // Check if the clicked polygon/extrusion belongs to the selected building
    if (hitPolygon && hitPolygon.length > 0) {
      const feature = hitPolygon[0];
      if (feature.properties?.contentId === this._selectedFeature.contentId) {
        this._isOnRotationHandle = false;
        this._isRotating = false;
        this._isDragging = true;
        this._mapboxViewer.toggleCamera(false);
        this._isEditing = true;
        this._canvas.addEventListener("pointerup", this.onPointerUpController);
        return;
      }
    }

    // If neither handle nor polygon is clicked, ignore
    this._isEditing = false;
    this._isRotating = false;
    this._isDragging = false;
  }

  @autobind
  onPointerMoveController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._isEditing) return;
    if (!this._selectedFeature) return;
    if (!this._dragStartPosition) return;

    this._isDragging = true;

    const canvasRect = this._canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top,
    };

    const currentLngLat = this._mapboxViewer._mapViewer.unproject([
      point.x,
      point.y,
    ]);

    if (this._isRotating || this._isOnRotationHandle) {
      // Rotation mode when dragging the handle
      this._isRotating = true;
      this._isDragging = false;

      if (this._rotationCenter) {
        const currentAngle = this.calculateAngleFromCenter(
          currentLngLat,
          this._rotationCenter
        );
        const deltaAngleRad = currentAngle - this._rotationStartAngle;
        const deltaAngleDeg = (deltaAngleRad * 180) / Math.PI;
        const newRotationDeg = this._originalRotationDeg + deltaAngleDeg;

        // Update building rotation (degrees in content)
        this._selectedFeature.updateContent({
          ...this._selectedFeature.content,
          properties: {
            ...this._selectedFeature.content.properties,
            rotation: newRotationDeg,
          },
        });
      }
    } else {
      // Position mode
      this._isRotating = false;
      this._isDragging = true;

      if (this._originalCoordinates) {
        // Calculate offset from drag start position
        const deltaLng = currentLngLat.lng - this._dragStartPosition!.lng;
        const deltaLat = currentLngLat.lat - this._dragStartPosition!.lat;

        // Move building by applying offset to coordinates
        const newCoordinates = this._originalCoordinates.map((coord) => [
          coord[0] + deltaLng,
          coord[1] + deltaLat,
        ]);

        // Update the building coordinates
        this._selectedFeature.updateContent({
          ...this._selectedFeature.content,
          coordinates: newCoordinates,
          properties: {
            ...this._selectedFeature.content.properties,
          },
        });
      }
    }
  }

  @autobind
  onPointerUpController() {
    if (!this._enabled || !this._selectedFeature) {
      this.cleanup();
      return;
    }

    this._mapboxViewer.toggleCamera(true);

    if (this._isDragging || this._isRotating) {
      // Finalize the changes
      this.complete();
    }

    this.cleanup();
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!this._selectedFeature) return;

    // Handle keyboard shortcuts for rotation
    if (event.key === "r" || event.key === "R") {
      // Reset rotation to 0
      this._selectedFeature.updateContent({
        ...this._selectedFeature.content,
        properties: {
          ...this._selectedFeature.content.properties,
          rotation: 0,
        },
      });
    }

    // Handle keyboard shortcuts for position reset
    if (event.key === "p" || event.key === "P") {
      // Reset to original position (you might want to store the original position)
      const originalCoordinates = [[7.46, 51.47]]; // Default position
      this._selectedFeature.updateContent({
        ...this._selectedFeature.content,
        coordinates: originalCoordinates,
        properties: {
          ...this._selectedFeature.content.properties,
        },
      });
    }
  }

  @autobind
  handleClick(event: PointerEvent) {
    this._mapboxViewer.toggleCamera(true);
    if (!this._enabled) return;
    if (!this._selectedFeature) return;
    if (event.button !== 0) return;

    // Only consider a click if no dragging occurred
    if (!this._onDownPosition.equals(this._onUpPosition)) {
      if (this._isEditing && (this._isDragging || this._isRotating)) {
        this.complete();
      }
    }
    this.cleanup();
  }

  @autobind
  complete() {
    if (!this._selectedFeature) return;

    const updatedContent = {
      ...this._selectedFeature.content,
      coordinates: this._selectedFeature.content.coordinates,
      properties: {
        ...this._selectedFeature.content.properties,
        rotation: this._selectedFeature.content.properties?.rotation || 0,
      },
    };

    // Dispatch completion event
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    this.dispatchEvent({
      type: EditorBuildingController.event.COMPLETE,
      content: updatedContent,
    });

    setTimeout(() => {
      this.detached();
      this._mapboxViewer.controllers.interactionController?.editorDetach();
    }, 100);
  }

  @autobind
  detached() {
    if (this._selectedFeature) {
      this._selectedFeature.setUnselected();
    }
    this.cleanup();
    this._selectedFeature = null;
  }

  @autobind
  private cleanup() {
    this._isEditing = false;
    this._isDragging = false;
    this._isRotating = false;
    this._isOnRotationHandle = false;
    this._dragStartPosition = null;
    this._originalCoordinates = null;
    this._originalRotationDeg = 0;
    this._rotationStartAngle = 0;
    this._rotationCenter = null;
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);
  }

  /**
   * Calculate angle from center point to target point
   */
  private calculateAngleFromCenter(
    target: { lng: number; lat: number },
    center: { lng: number; lat: number }
  ): number {
    const deltaLng = target.lng - center.lng;
    const deltaLat = target.lat - center.lat;
    return Math.atan2(deltaLat, deltaLng);
  }

  @autobind
  dispose() {
    this._canvas.removeEventListener(
      "pointerdown",
      this.onPointerDownController
    );
    this._canvas.removeEventListener(
      "pointermove",
      this.onPointerMoveController
    );
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);
    this._canvas.removeEventListener("keydown", this.handleKeyDown);
    this.cleanup();
  }
}

export default EditorBuildingController;
